import React, { useEffect, useRef, useState } from "react";
import {
  createMembershipOrder,
  membershipWindowClosed,
  startMembershipFee,
  startMembershipFeeSignedIn,
  verifyMembershipPayment,
} from "../apis/paymentsApi";
import { openRazorpayCheckout, rupees } from "../Utils/razorpayCheckout";
import { brandName } from "../config/brand";
import "../styles/membershipFee.css";

// The one-time membership fee (fpo MembershipFeeService). There is no approval step: the account is active as soon
// as it is created, with guest access until this fee is paid, and as a member once it is paid. Shown right after
// registering (Register.jsx) and on the Sign-in page (LoginPage.jsx), started with the email / mobile and password
// just typed; and, for a signed-in guest, from the guest banner (GuestMembershipBanner, signedIn). When no fee is
// due it shows nothing and calls onFinished("none"); "error" when it couldn't check.
// "Continue as Guest" (onFinished("guest")) is always offered, and is the way on after a cancelled or failed payment:
// nothing is charged and the membership isn't given. Right after registering it does not sign in: Register.jsx goes
// back to the form with the Guest account type chosen, to register a Guest account (the registration whose fee isn't
// paid stays incomplete, status pending_payment, and is used again). For a signed-in guest it is "Not now".
// Styles: styles/membershipFee.css.

const STEPS = ["Account created", "Membership fee", "Ready"];

function Steps({ now }) {
  return (
    <ol className="mf-steps" aria-label="Registration steps">
      {STEPS.map((label, i) => (
        <React.Fragment key={label}>
          {i > 0 && <li className="mf-step-line" aria-hidden="true" />}
          <li className={`mf-step ${i < now ? "is-done" : i === now ? "is-now" : ""}`} aria-current={i === now ? "step" : undefined}>
            <span className="mf-step-dot" aria-hidden="true">{i < now ? <i className="fas fa-check" /> : i + 1}</span>
            <span className="mf-step-text">{label}</span>
          </li>
        </React.Fragment>
      ))}
    </ol>
  );
}

function Header({ title, subtitle, step }) {
  return (
    <div className="mf-head">
      <div className="mf-brand">
        <img className="mf-logo" src="/assets/img/login/feed-world-mark.png" alt="" />
        <div>
          <h2 id="mf-title">{title}</h2>
          <p>{subtitle}</p>
        </div>
      </div>
      <Steps now={step} />
    </div>
  );
}

export default function MembershipFeePayment({ login, password, signedIn = false, onFinished }) {
  const [state, setState] = useState("checking");   // checking | due | paying | paid | confirming
  const [fee, setFee] = useState(null);
  const [message, setMessage] = useState(null);
  const [triedPaying, setTriedPaying] = useState(false);
  const [receiptNo, setReceiptNo] = useState(null);
  const orGuest = signedIn ? "pay later" : "continue as a Guest";

  // Started once per window: each start gives a new payment token and the server keeps only the latest, so a
  // second start (React's development double effects, a re-render) would leave this window with a stale token
  // and the payment would be refused ("This payment link has expired").
  const startedFor = useRef(null);
  useEffect(() => {
    const key = signedIn ? "signed-in" : `${login}`;
    if (startedFor.current === key) return;
    startedFor.current = key;
    (signedIn ? startMembershipFeeSignedIn() : startMembershipFee(login, password))
      .then((res) => {
        const data = res.data?.data;
        if (data?.required) {
          setFee(data);
          setState("due");
        } else {
          onFinished(data?.status === "PAID" ? "paid" : "none");
        }
      })
      // Couldn't check (e.g. the server is down): don't say "no fee" — the guest banner offers it again later
      .catch(() => onFinished("error"));
  }, [login, password, signedIn]); // eslint-disable-line react-hooks/exhaustive-deps

  const pay = async () => {
    setState("paying");
    setMessage(null);
    setTriedPaying(true);
    try {
      const order = (await createMembershipOrder(fee.token)).data?.data;
      const result = await openRazorpayCheckout(order);
      const tx = result.status === "paid"
        ? (await verifyMembershipPayment(fee.token, result.orderId, result.paymentId, result.signature)).data?.data
        : (await membershipWindowClosed(fee.token, order.orderId)).data?.data;
      if (tx?.status === "PAID") {
        setReceiptNo(tx.receiptNo);
        setState("paid");
      } else if (result.status === "paid" || tx?.status === "CREATED") {
        setState("confirming");
      } else {
        setState("due");
        setMessage(result.status === "failed"
          ? `The payment didn't go through: ${result.reason} Nothing was charged. You can try again, or ${orGuest}.`
          : `Payment cancelled. Nothing was charged. You can try again, or ${orGuest}.`);
      }
    } catch (err) {
      setState("due");
      setMessage((err?.response?.data?.error?.message || err?.response?.data?.message || err?.message || "Couldn't start the payment.")
        + ` You can try again, or ${orGuest}.`);
    }
  };

  if (state === "checking") return null;

  const a = fee?.amounts || {};
  const firstName = (fee?.name || "").trim().split(/\s+/)[0];
  const guestLabel = signedIn ? "Not now" : "Continue as Guest";

  return (
    <div className="mf-backdrop">
      <div className="mf-card" role="dialog" aria-modal="true" aria-labelledby="mf-title">
        {(state === "due" || state === "paying") && (
          <>
            <Header title={signedIn ? "Become a member" : "One last step"} subtitle={`Become a member of ${brandName()}`} step={1} />
            <div className="mf-body">
              <p className="mf-intro">
                {firstName ? <>Welcome, <b>{firstName}</b>! </> : "Welcome! "}
                {signedIn
                  ? "Pay the one-time membership fee to unlock the member services. Your access is updated as soon as the payment is confirmed."
                  : "Your mobile number is verified. Pay the one-time membership fee to become a member, or continue as a Guest: register a Guest account to look around with limited access."}
              </p>

              <div className="mf-bill">
                <div className="mf-bill-item">
                  <i className="fas fa-id-card" aria-hidden="true" />
                  <span>{fee.item}</span>
                  <span className="mf-bill-badge">Paid once</span>
                </div>
                {/* The GST split only when the administrator shows it (Membership & Payment Settings); otherwise just the total */}
                {a.showGst && Number(a.gst) > 0 && (
                  <>
                    <div className="mf-bill-row"><span>Membership fee</span><span className="mf-num">{rupees(a.base)}</span></div>
                    <div className="mf-bill-row"><span>GST ({Number(a.gstRate)}%)</span><span className="mf-num">{rupees(a.gst)}</span></div>
                  </>
                )}
                <div className="mf-bill-total"><span>Total</span><strong>{rupees(a.total)}</strong></div>
              </div>

              {message && (
                <div className="mf-alert" role="alert">
                  <i className="fas fa-exclamation-circle" aria-hidden="true" style={{ marginTop: 2 }} />
                  <span>{message}</span>
                </div>
              )}

              <button type="button" className="mf-pay" onClick={pay} disabled={state === "paying"}>
                {state === "paying"
                  ? <><span className="spinner-border spinner-border-sm" aria-hidden="true" /> Opening secure payment…</>
                  : <><i className="fas fa-lock" aria-hidden="true" /> {triedPaying ? "Try again" : "Pay"} {rupees(a.total)}</>}
              </button>
              <button type="button" className={`mf-later ${triedPaying && !signedIn ? "is-strong" : ""}`}
                onClick={() => onFinished("guest")} disabled={state === "paying"}>
                {!signedIn && <i className="fas fa-user" aria-hidden="true" />} {guestLabel}
              </button>

              <div className="mf-methods" aria-label="Ways to pay">
                <span className="mf-method"><i className="fas fa-mobile-alt" aria-hidden="true" /> UPI</span>
                <span className="mf-method"><i className="far fa-credit-card" aria-hidden="true" /> Cards</span>
                <span className="mf-method"><i className="fas fa-university" aria-hidden="true" /> Net banking</span>
                <span className="mf-method"><i className="fas fa-wallet" aria-hidden="true" /> Wallets</span>
              </div>
              <p className="mf-secure"><i className="fas fa-shield-alt" aria-hidden="true" />Secured by Razorpay · a receipt is emailed to you if you gave an email</p>
              {!signedIn && (
                <p className="mf-note">
                  Continue as Guest takes you back to the registration form to register a Guest account with the same
                  details. You can become a member later from the banner at the top of the page.
                </p>
              )}
            </div>
          </>
        )}

        {state === "paid" && (
          <div className="mf-done">
            <div className="mf-check" aria-hidden="true">
              <svg viewBox="0 0 40 40"><path d="M11 21 l6 6 l12 -13" /></svg>
            </div>
            <h2 id="mf-title">Payment successful</h2>
            <p>Thank you! Your membership fee is paid.</p>
            <span className="mf-receipt">Receipt {receiptNo}</span>
            <p>Your membership is being activated; it's ready in a minute.{signedIn ? " Sign in again if a page still shows guest access." : ""}</p>
            <button type="button" className="mf-pay" onClick={() => onFinished("paid")}>Continue</button>
          </div>
        )}

        {state === "confirming" && (
          <div className="mf-done">
            <div className="mf-check is-wait" aria-hidden="true"><i className="fas fa-hourglass-half" /></div>
            <h2 id="mf-title">Confirming your payment</h2>
            <p>We're confirming your payment with the bank. This usually takes a few minutes; nothing more is needed from you.</p>
            {signedIn
              ? <p>Meanwhile you can use {brandName()} as a guest. Your membership starts as soon as the payment is confirmed.</p>
              : <p>Your account opens as soon as the payment is confirmed. Then sign in with your mobile number and password.</p>}
            <button type="button" className="mf-pay" onClick={() => onFinished("confirming")}>Continue</button>
          </div>
        )}
      </div>
    </div>
  );
}
